import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, TextField } from '@mui/material';
import LeistungenOverviewList from '../components/content/LeistungenOverviewList';
import BeratungParallaxImages from '../components/content/BeratungParallaxImages';
import EntwicklungenCarousel from '../components/content/EntwicklungenCarousel';
import { motion } from 'framer-motion';
import SiteTopBar, { TOPBAR_HEIGHT } from '../components/layout/SiteTopBar';
import { ScrollBlurHeadline } from '../components/motion/ScrollReveal';
import ScrollFade from '../components/motion/ScrollFade';
import { ScrollStagger, ScrollStaggerItem } from '../components/motion/ScrollStagger';
import { useTranslation } from '../content/useTranslation';
import { useMenu } from '../context/MenuContext';
import { useIntroComplete } from '../context/IntroContext';
import { PATHS } from '../navigation/paths';
import {
  COLORS,
  FONTS,
  TYPO,
  SPACING,
  RADIUS,
  BUTTON,
} from '../theme/designTokens';
import {
  METHODIK_CARD_BORDER,
  OVERVIEW_CARD_MD_WIDTH,
  methodikCardBodySx,
  methodikCardClickableSx,
  methodikCardTitleSx,
} from '../theme/overviewCardStyles';
import { getTopBarAppearance } from '../utils/topBarSectionColor';
import { applyHeroScrollFade } from '../utils/heroScrollFade';
import { usePageSeo } from '../hooks/usePageSeo';
import { DEFAULT_SEO } from '../seo/siteConfig';
import { buildHomePageJsonLd } from '../seo/jsonLd';
import { useSeoAllowed } from '../components/SitePasswordGate';
import { useLang } from '../context/LanguageContext';
import { consumePendingHomeScroll, scrollToPendingHomeSection, scrollToSectionById } from '../utils/smoothScroll';
import { openCookieSettings } from '../utils/cookieConsent';
import ResponsiveImage from '../components/ResponsiveImage';

const HOME_JSON_LD = buildHomePageJsonLd();

const btnLight = BUTTON.light;
const btnDark = BUTTON.dark;

/* ─── Methodik / Entwicklungen cards ─── */

const overviewCardsGridSx = {
  display: 'grid',
  width: '100%',
  maxWidth: SPACING.maxContent,
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    md: `repeat(auto-fill, ${OVERVIEW_CARD_MD_WIDTH}px)`,
  },
  justifyContent: 'start',
  gap: '15px',
} as const;

/* ─── Component ─── */

const SCROLL_INDICATOR_FADE_PX = 100;

const FOOTER_LINKS = [
  { label: 'Instagram', href: 'https://www.instagram.com/zoellnerlorenz_familienrecht', external: true },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/144556911/', external: true },
  { label: 'Impressum', href: PATHS.impressum, external: false },
  { label: 'Datenschutz', href: PATHS.datenschutz, external: false },
  { label: 'Widerrufsbelehrung', href: PATHS.widerruf, external: false },
] as const;

const teamContactLineSx = {
  fontFamily: FONTS.sans,
  fontWeight: 300,
  fontSize: '12px',
  lineHeight: 1.6,
  color: COLORS.textMutedOnLight,
  display: 'block',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
} as const;

const footerFieldSx = {
  ...TYPO.inputField,
  borderBottom: '1.05px solid #fff',
  pb: '12px',
  pt: '3px',
  '& input::placeholder, & textarea::placeholder': { color: COLORS.inputPlaceholder, opacity: 1 },
  '& input, & textarea': { color: '#fff' },
};

const HomePage: React.FC = () => {
  const { t, methodik, team, teamIntro, leistungen, aktuelles, legalLabel } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const introComplete = useIntroComplete();
  const logoWrapRef = useRef<HTMLDivElement>(null);
  const scrollIndicatorRef = useRef<HTMLDivElement>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef(0);
  const { setAppearance, registerContentScrollRef, menuVisible } = useMenu();
  const seoAllowed = useSeoAllowed();
  const { lang } = useLang();
  const [stickyTopBarVisible, setStickyTopBarVisible] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const isEnglishHome = location.pathname.replace(/\/+$/, '') === PATHS.en;
  usePageSeo({
    title: isEnglishHome
      ? 'Family Law & Mediation in Munich | Zöllner Lorenz'
      : DEFAULT_SEO.title,
    description: isEnglishHome
      ? 'Family law, mediation and cooperative practice in Munich — for separation, divorce, maintenance and financial matters.'
      : DEFAULT_SEO.description,
    path: isEnglishHome ? PATHS.en : '/',
    locale: lang === 'de' ? 'de_DE' : 'en_US',
    noindex: !seoAllowed,
    jsonLd: HOME_JSON_LD,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;
    setFormStatus('sending');

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID ?? '';
    const templateNotify = process.env.REACT_APP_EMAILJS_TEMPLATE_NOTIFY ?? '';
    const templateConfirm = process.env.REACT_APP_EMAILJS_TEMPLATE_CONFIRM ?? '';
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY ?? '';

    try {
      const emailjs = await import('@emailjs/browser');
      await emailjs.default.sendForm(serviceId, templateNotify, formRef.current, publicKey);
      await emailjs.default.sendForm(serviceId, templateConfirm, formRef.current, publicKey);
      // Ads conversion is the existing GTM form tag (label H_xFCL-8itIcEPGIwpxE).
      // It listens for the submit event. A second hit from here would count twice.
      setFormStatus('sent');
      formRef.current.reset();
    } catch {
      setFormStatus('error');
    }
  };

  useEffect(() => {
    registerContentScrollRef(contentScrollRef.current);
    return () => registerContentScrollRef(null);
  }, [registerContentScrollRef]);

  useEffect(() => {
    const scrollTo = consumePendingHomeScroll();
    if (scrollTo) {
      scrollToPendingHomeSection(scrollTo);
    }

    // Legacy: clear scrollTo from history so refresh doesn't re-scroll
    if ((location.state as { scrollTo?: string } | null)?.scrollTo) {
      navigate('.', { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyScrollStyles = useCallback(() => {
    const y = window.scrollY;
    const vh = window.innerHeight;

    applyHeroScrollFade(logoWrapRef.current, y, introComplete, true);
    applyHeroScrollFade(document.getElementById('lcp-hero-copy'), y, introComplete, false);

    if (scrollIndicatorRef.current) {
      if (!introComplete) {
        scrollIndicatorRef.current.style.pointerEvents = 'none';
      } else {
        const fade = Math.max(0, 1 - y / SCROLL_INDICATOR_FADE_PX);
        scrollIndicatorRef.current.style.opacity = String(fade * 0.45);
        scrollIndicatorRef.current.style.pointerEvents = fade > 0 ? 'auto' : 'none';
      }
    }

    const showSticky = y > vh;
    setStickyTopBarVisible(showSticky);

    if (!menuVisible) {
      if (y <= vh) {
        setAppearance({ bg: 'transparent', theme: 'dark' });
      } else if (contentScrollRef.current) {
        const { bg, theme } = getTopBarAppearance(
          contentScrollRef.current,
          TOPBAR_HEIGHT,
          COLORS.bg,
        );
        setAppearance({ bg, theme });
      }
    }
    // Refs are stable — intentionally omitted from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introComplete, setAppearance, menuVisible]);

  useEffect(() => {
    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(applyScrollStyles);
    };
    applyScrollStyles();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, [applyScrollStyles]);

  useEffect(() => {
    if (introComplete) applyScrollStyles();
  }, [introComplete, applyScrollStyles]);

  useEffect(() => {
    const el = document.getElementById('lcp-hero-copy');
    if (el && el.textContent !== t.heroHeadline) el.textContent = t.heroHeadline;
  }, [t.heroHeadline]);

  return (
    <>
      {/* ════════════════════════════════════════════
          HERO — sticky, content scrolls over it
         ════════════════════════════════════════════ */}
      <Box
        sx={{
          height: '100svh',
          position: 'sticky',
          top: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          maxWidth: { xs: '100%', md: 'none' },
          zIndex: 0,
        }}
      >
        {/* Photo + shade live in index.html so they paint before JavaScript. */}

        {/* Hero topbar removed — single sticky topbar with gradient blur is always visible */}

        <Box ref={logoWrapRef} />

        {/* Headline lives in index.html so it is on screen before JavaScript. */}

        {/* Scroll indicator */}
        <Box
          ref={scrollIndicatorRef}
          sx={{
            position: 'absolute',
            bottom: '60px',
            left: 0,
            right: 0,
            zIndex: 1,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {introComplete && (
            <motion.div
              animate={{ y: [0, 5, 0] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Box
                sx={{
                  width: '1px',
                  height: { xs: '18px', md: '22px' },
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0.35), transparent)',
                }}
              />
            </motion.div>
          )}
        </Box>
      </Box>

      <SiteTopBar variant="sticky" visible={introComplete} />

      {/* ════════════════════════════════════════════
          CONTENT — scrolls over hero
         ════════════════════════════════════════════ */}
      <Box
        ref={contentScrollRef}
        sx={{
          position: 'relative',
          zIndex: 1,
          backgroundColor: COLORS.bg,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          overflow: 'hidden',
          maxWidth: { xs: '100%', md: 'none' },
        }}
      >
        {/* ─── ÜBER UNS ─── */}
        <Box
          id="ueber-uns"
          data-section-bg={COLORS.bg}
          data-section-theme="light"
          sx={{
            ...SPACING.sectionViewport,
            px: SPACING.sectionPaddingX,
            paddingTop: { xs: '80px', md: '120px' },
            minHeight: 'auto',
            paddingBottom: { xs: '80px', md: '120px' },
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 446px' },
              gridTemplateRows: { md: 'auto auto auto' },
              columnGap: { md: '80px' },
              rowGap: { xs: '32px', md: '12px' },
              alignItems: { md: 'start' },
            }}
          >
            <Typography
              sx={{
                ...TYPO.eyebrow,
                color: COLORS.text,
                gridColumn: '1',
                gridRow: { md: '1' },
              }}
            >
              {t.sections.ueberUns.eyebrow}
            </Typography>

            <ScrollBlurHeadline
              sx={{
                gridColumn: '1',
                gridRow: { md: '2' },
              }}
            >
              <Typography
                sx={{
                  ...TYPO.aboutIntro,
                  maxWidth: 925,
                  textWrap: 'pretty',
                }}
              >
                {t.sections.ueberUns.headline}
              </Typography>
            </ScrollBlurHeadline>

            <ScrollFade
              fadeOut={false}
              sx={{
                gridColumn: { xs: '1', md: '2' },
                gridRow: { xs: 'auto', md: '3' },
                justifySelf: { md: 'end' },
                width: { md: 446 },
                mt: { xs: '24px', md: '88px' },
                /** Desktop: 2× Section-Padding (71px) + 12px Abstand vom rechten Rand */
                mr: { md: '83px' },
              }}
            >
              <Typography
                sx={{
                  ...TYPO.aboutSide,
                  maxWidth: { xs: '100%', md: 446 },
                }}
              >
                {t.sections.ueberUns.body}
              </Typography>
            </ScrollFade>
          </Box>
        </Box>

        {/* ─── VORGEHENSWEISEN ─── */}
        <Box
          id="methodik"
          data-section-bg={COLORS.bgWhite}
          data-section-theme="light"
          sx={{
            ...SPACING.sectionViewport,
            backgroundColor: COLORS.bgWhite,
            px: SPACING.sectionPaddingX,
            paddingTop: { xs: '100px', md: '112px' },
            paddingBottom: { xs: '160px', md: '200px' },
          }}
        >
          <Typography
            sx={{
              ...TYPO.eyebrow,
              color: COLORS.text,
              mb: SPACING.eyebrowToHeadline,
            }}
          >
            {t.sections.methodik.eyebrow}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { md: 'flex-start' },
              gap: { xs: '18px', md: '30px' },
              mb: { xs: '120px', md: '180px' },
            }}
          >
            <ScrollBlurHeadline sx={{ flex: 1 }}>
              <Box component="h2" sx={{ m: 0 }}>
                <Box
                  component="span"
                  sx={{ ...TYPO.methodikHeadlineSans, display: 'block' }}
                >
                  {t.sections.methodik.headlineSans1}
                </Box>
                {t.sections.methodik.headlineSans2 && (
                <Box
                  component="span"
                  sx={{ ...TYPO.methodikHeadlineSans, display: 'block' }}
                >
                  {t.sections.methodik.headlineSans2}
                </Box>
                )}
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    pl: { xs: 0, md: '3.35ch' },
                  }}
                >
                  {t.sections.methodik.headlineSans3 && (
                  <Box component="span" sx={TYPO.methodikHeadlineSans}>
                    {t.sections.methodik.headlineSans3}{' '}
                  </Box>
                  )}
                  <Box component="span" sx={{ ...TYPO.methodikHeadlineAccent, lineHeight: 1.1 }}>
                    {t.sections.methodik.headlineAccent}
                  </Box>
                </Box>
              </Box>
            </ScrollBlurHeadline>

            <Box
              sx={{
                flex: { xs: 1, md: 0.6 },
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '18px', md: '32px' },
                justifyContent: 'flex-start',
                alignItems: { xs: 'stretch', md: 'flex-end' },
                maxWidth: { md: 440 },
                ml: { md: 'auto' },
              }}
            >
              <ScrollFade fadeOut={false} sx={{ width: '100%', maxWidth: { md: 440 } }}>
              <Typography sx={{ ...TYPO.bodyLight, textAlign: 'left' }}>
                {t.sections.methodik.body}
              </Typography>
              </ScrollFade>
              <Button
                sx={{
                  ...btnLight,
                  alignSelf: { xs: 'flex-start', md: 'flex-end' },
                }}
                onClick={() => scrollToSectionById('leistungen')}
              >
                {t.sections.methodik.servicesButton}
              </Button>
            </Box>
          </Box>

          {/* 3 Cards — linksbündig im Content-Raster */}
          <ScrollStagger sx={overviewCardsGridSx}>
            {methodik.map((card) => (
              <ScrollStaggerItem
                key={card.slug}
                component={Link}
                to={PATHS.methodikDetail(card.slug)}
                state={{ fromLanding: true }}
                sx={methodikCardClickableSx}
              >
                <Typography sx={methodikCardTitleSx}>{card.title}</Typography>
                <Typography sx={{ ...methodikCardBodySx, marginTop: 'auto' }}>{card.description}</Typography>
              </ScrollStaggerItem>
            ))}
          </ScrollStagger>
        </Box>

        {/* ─── ÜBERZEUGUNG — static bg; only content fades in ─── */}
        <Box
          data-section-bg={COLORS.bgDark}
          data-section-theme="dark"
          sx={{
            ...SPACING.sectionViewport,
            backgroundColor: COLORS.bgDark,
            px: SPACING.sectionPaddingX,
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 900,
              mx: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
          <Typography
            sx={{
              ...TYPO.eyebrow,
              color: COLORS.textOnDarkMuted,
              width: '100%',
              textAlign: 'center',
              mb: { xs: '7px', md: '10px' },
            }}
          >
            {t.sections.ueberzeugung.eyebrow}
          </Typography>

          <ScrollBlurHeadline
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              width: '100%',
            }}
          >
            <Typography
              sx={{
                ...TYPO.guillemet,
                color: '#fff',
                mb: '10px',
                width: '100%',
                textAlign: 'center',
              }}
            >
              »
            </Typography>

            <Typography
              component="p"
              sx={{
                ...TYPO.bigHeadline,
                color: '#fff',
                width: '100%',
                maxWidth: 900,
                mx: 'auto',
                textAlign: 'center',
                mb: '10px',
              }}
            >
              {t.sections.ueberzeugung.quote}{' '}
              <Box
                component="span"
                sx={{ fontStyle: 'italic', color: COLORS.textEmphasis }}
              >
                {t.sections.ueberzeugung.quoteAccent}
              </Box>
            </Typography>

            <Typography
              sx={{
                ...TYPO.guillemet,
                color: '#fff',
                mb: { xs: '22px', md: '28px' },
                width: '100%',
                textAlign: 'center',
              }}
            >
              «
            </Typography>
          </ScrollBlurHeadline>

          <Typography
            sx={{
              ...TYPO.bodyOnDark,
              width: '100%',
              maxWidth: { xs: 480, md: 500 },
              mx: 'auto',
              px: { xs: '20px', sm: '16px', md: '24px' },
              boxSizing: 'border-box',
              textAlign: 'center',
            }}
          >
            {t.sections.ueberzeugung.body}
          </Typography>
          </Box>
        </Box>

        {/* ─── TEAM ─── */}
        <Box
          id="team"
          data-section-bg={COLORS.bgWhite}
          data-section-theme="light"
          sx={{
            ...SPACING.sectionViewport,
            backgroundColor: COLORS.bgWhite,
            px: SPACING.sectionPaddingX,
            paddingTop: { xs: '100px', md: '112px' },
            paddingBottom: { xs: '80px', md: '140px' },
          }}
        >
          <Typography
            sx={{
              ...TYPO.eyebrow,
              color: COLORS.text,
              mb: SPACING.eyebrowToHeadline,
            }}
          >
            {t.sections.team.eyebrow}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { md: 'flex-start' },
              gap: { xs: '24px', md: '40px' },
              mb: { xs: '48px', md: '72px' },
            }}
          >
            <ScrollBlurHeadline>
              <Box component="h2" sx={{ m: 0 }}>
                <Box
                  component="span"
                  sx={{
                    ...TYPO.methodikHeadlineSans,
                    display: 'block',
                    pl: { xs: 0, md: '2.6ch' },
                  }}
                >
                  {t.sections.team.headlineSans}
                </Box>
                <Box component="span" sx={{ display: 'block' }}>
                  <Box component="span" sx={{ ...TYPO.methodikHeadlineAccent, lineHeight: 1.1 }}>
                    {t.sections.team.headlineAccent}
                  </Box>
                </Box>
              </Box>
            </ScrollBlurHeadline>

            <Box sx={{ maxWidth: 440 }}>
              <Typography sx={{ ...TYPO.bodyLight }}>{teamIntro}</Typography>
            </Box>
          </Box>

          {/* Team cards — Figma: zentriert, Foto 360×466, Button 150px */}
          <ScrollStagger
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'center',
              gap: { xs: '80px', sm: '120px' },
            }}
          >
            {team.map((member) => {
              const email = member.email ?? '';

              return (
                <ScrollStaggerItem
                  key={member.title}
                  sx={{
                    width: { xs: '100%', md: 268 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: '75%', md: 268 },
                      maxWidth: { xs: '75%', md: 299 },
                      height: { xs: 293, sm: 386 },
                      borderRadius: 0,
                      overflow: 'hidden',
                      flexShrink: 0,
                      mx: 'auto',
                    }}
                  >
                    <ResponsiveImage
                      base={(member.imageSrc ?? '').includes('susanna') ? 'team-susanna' : 'team-marlene'}
                      widths={(member.imageSrc ?? '').includes('susanna') ? [360, 474] : [360, 682]}
                      sizes="(min-width: 900px) 268px, 75vw"
                      alt={member.title}
                      loading="lazy"
                      imgSx={{
                        objectFit: 'cover',
                        objectPosition: {
                          xs: member.imagePositionMobile ?? member.imagePosition ?? 'center center',
                          sm: member.imagePosition ?? 'center center',
                        },
                        transform: {
                          xs: (member.imageScaleMobile ?? member.imageScale)
                            ? `scale(${member.imageScaleMobile ?? member.imageScale})`
                            : undefined,
                          sm: (member.imageScaleDesktop ?? member.imageScale)
                            ? `scale(${member.imageScaleDesktop ?? member.imageScale})`
                            : undefined,
                        },
                        transformOrigin: member.imageTransformOrigin ?? 'center center',
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '5px',
                      width: '100%',
                      mt: '20px',
                      mb: '16px',
                      textAlign: 'center',
                    }}
                  >
                    <Typography sx={{ ...TYPO.teamName, width: '100%' }}>
                      {member.title}
                    </Typography>
                    <Box sx={{ mt: '4px' }}>
                      <Typography
                        component="a"
                        href="tel:+498924889752"
                        sx={teamContactLineSx}
                      >
                        {t.footer.telefon}: +49 89 24889752
                      </Typography>
                      {email && (
                        <Typography
                          component="a"
                          href={`mailto:${email}`}
                          sx={teamContactLineSx}
                        >
                          {t.footer.email}: {email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Button
                    component={Link}
                    to={PATHS.teamDetail(member.slug)}
                    state={{ fromLanding: true }}
                    sx={btnLight}
                  >
                    {t.mehrErfahren}
                  </Button>
                </ScrollStaggerItem>
              );
            })}
          </ScrollStagger>
        </Box>

        <BeratungParallaxImages />

        {/* ─── LEISTUNGEN ─── */}
        <Box
          id="leistungen"
          data-section-bg={COLORS.bgWhite}
          data-section-theme="light"
          sx={{
            ...SPACING.sectionViewport,
            backgroundColor: COLORS.bgWhite,
            px: SPACING.sectionPaddingX,
            paddingTop: { xs: '100px', md: '112px' },
          }}
        >
          <Typography
            sx={{
              ...TYPO.eyebrow,
              color: COLORS.text,
              mb: SPACING.eyebrowToHeadline,
            }}
          >
            {t.sections.leistungen.eyebrow}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'stretch' },
              gap: { xs: '24px', md: '40px' },
              mb: { xs: '48px', md: '72px' },
            }}
          >
            <ScrollBlurHeadline sx={{ flex: 1, minWidth: 0 }}>
              <Box component="h2" sx={{ m: 0 }}>
                <Box
                  component="span"
                  sx={{ ...TYPO.methodikHeadlineSans, display: 'block' }}
                >
                  {t.sections.leistungen.headlineSans}
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    pl: { xs: 0, md: '3.35ch' },
                  }}
                >
                  <Box component="span" sx={{ ...TYPO.methodikHeadlineAccent, lineHeight: 1.1 }}>
                    {t.sections.leistungen.headlineAccent}
                  </Box>
                </Box>
              </Box>
            </ScrollBlurHeadline>

            <ScrollFade fadeOut={false}>
              <Box
                sx={{
                  flex: { xs: 1, md: '0 1 auto' },
                  width: { md: 'auto' },
                  maxWidth: { xs: '100%', md: 440 },
                  minWidth: { md: 280 },
                  ml: { md: 'auto' },
                  display: 'flex',
                  alignItems: { md: 'flex-start' },
                }}
              >
                <Typography sx={{ ...TYPO.bodyLight, textAlign: 'left', m: 0 }}>
                  {t.sections.leistungen.intro1}{' '}
                  {t.sections.leistungen.intro2}
                </Typography>
              </Box>
            </ScrollFade>
          </Box>

          <ScrollFade fadeOut={false}>
            <LeistungenOverviewList items={leistungen} textColor={COLORS.text} bodyColor={COLORS.textMutedOnLight} />
          </ScrollFade>
        </Box>

        {/* ─── ENTWICKLUNGEN ─── */}
        <Box
          id="aktuelles"
          data-section-bg={COLORS.bg}
          data-section-theme="light"
          sx={{
            ...SPACING.sectionViewport,
            backgroundColor: COLORS.bg,
            px: SPACING.sectionPaddingX,
            paddingTop: { xs: '100px', md: '112px' },
          }}
        >
          <Typography
            sx={{
              ...TYPO.eyebrow,
              color: COLORS.text,
              mb: SPACING.eyebrowToHeadline,
            }}
          >
            {t.sections.aktuelles.eyebrow}
          </Typography>

          <Box sx={{ maxWidth: 561, mb: { xs: '64px', md: '96px' } }}>
            <ScrollBlurHeadline sx={{ mb: { xs: '40px', md: '56px' } }}>
              <Box component="h2" sx={{ m: 0 }}>
                <Box
                  component="span"
                  sx={{ ...TYPO.methodikHeadlineSans, display: 'block' }}
                >
                  {t.sections.aktuelles.headlineSans1}
                </Box>
                <Box
                  component="span"
                  sx={{ ...TYPO.methodikHeadlineSans, display: 'block' }}
                >
                  {t.sections.aktuelles.headlineSans2}
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    pl: { xs: 0, md: '3.35ch' },
                  }}
                >
                  <Box component="span" sx={TYPO.methodikHeadlineSans}>
                    {t.sections.aktuelles.headlineSans3}{' '}
                  </Box>
                  <Box component="span" sx={TYPO.methodikHeadlineAccent}>
                    {t.sections.aktuelles.headlineAccent}
                  </Box>
                </Box>
              </Box>
            </ScrollBlurHeadline>

            <ScrollFade fadeOut={false}>
            <Typography sx={{ ...TYPO.bodyLight, textAlign: 'left' }}>
              {t.sections.aktuelles.body}
            </Typography>
            </ScrollFade>
          </Box>

          <EntwicklungenCarousel
            items={aktuelles}
            prevLabel={t.sections.aktuelles.carouselPrev}
            nextLabel={t.sections.aktuelles.carouselNext}
          />
        </Box>

        {/* ─── FOOTER / KONTAKT — Figma 95:649 ─── */}
        <Box
          id="kontakt"
          data-section-bg={COLORS.bgDark}
          data-section-theme="dark"
          sx={{
            backgroundColor: COLORS.bgDark,
            px: SPACING.sectionPaddingX,
            pt: { xs: '80px', md: '100px' },
            pb: { xs: '60px', md: '54px' },
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100svh',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '678px 1fr' },
              columnGap: { lg: '129px' },
              rowGap: { xs: '48px', lg: 0 },
              alignItems: 'stretch',
              minHeight: { lg: 580 },
            }}
          >
              {/* ── Linke Spalte: Logo, Intro, Formular ── */}
              <Box sx={{ maxWidth: { lg: 678 } }}>
                <Box
                  component="img"
                  src="/images/logo-zl.svg"
                  alt="Zöllner Lorenz"
                  sx={{
                    width: { xs: 200, md: 253 },
                    height: 'auto',
                    display: 'block',
                    mb: { xs: '28px', md: '35px' },
                  }}
                />

                <Typography
                  sx={{
                    ...TYPO.bodyLargeLight,
                    maxWidth: 562,
                    mb: { xs: '32px', md: '40px' },
                  }}
                >
                  {t.sections.kontakt.intro}
                </Typography>

                <Box
                  component="form"
                  ref={formRef}
                  onSubmit={handleSubmit}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: '16.8px',
                  }}
                >
                  {([t.form.vorname, t.form.nachname] as const).map((placeholder) => (
                    <TextField
                      key={placeholder}
                      name={placeholder === t.form.vorname ? 'vorname' : 'nachname'}
                      variant="standard"
                      placeholder={placeholder}
                      fullWidth
                      required
                      slotProps={{
                        htmlInput: { 'aria-label': placeholder },
                        input: {
                          disableUnderline: true,
                          sx: footerFieldSx,
                        },
                      }}
                    />
                  ))}
                  <TextField
                    name="email"
                    type="email"
                    variant="standard"
                    placeholder={t.form.email}
                    fullWidth
                    required
                    sx={{ gridColumn: { sm: '1 / -1' } }}
                    slotProps={{
                      htmlInput: { 'aria-label': t.form.email },
                      input: {
                        disableUnderline: true,
                        sx: footerFieldSx,
                      },
                    }}
                  />
                  <TextField
                    name="telefon"
                    variant="standard"
                    placeholder={t.form.telefon}
                    fullWidth
                    sx={{ gridColumn: { sm: '1 / -1' } }}
                    slotProps={{
                      htmlInput: { 'aria-label': t.form.telefon },
                      input: {
                        disableUnderline: true,
                        sx: footerFieldSx,
                      },
                    }}
                  />
                  <TextField
                    name="nachricht"
                    variant="standard"
                    placeholder={t.form.nachricht}
                    fullWidth
                    multiline
                    minRows={6}
                    required
                    sx={{
                      gridColumn: { sm: '1 / -1' },
                      '& .MuiInputBase-root': {
                        alignItems: 'flex-start',
                        minHeight: 186,
                        backdropFilter: 'blur(50px)',
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        borderRadius: `${RADIUS.sm}px`,
                        p: '11px 11px 16px',
                        boxSizing: 'border-box',
                      },
                    }}
                    slotProps={{
                      htmlInput: { 'aria-label': t.form.nachricht },
                      input: {
                        disableUnderline: true,
                        sx: {
                          ...footerFieldSx,
                          borderBottom: 'none',
                          pb: 0,
                          pt: 0,
                          height: '100%',
                          alignItems: 'flex-start',
                        },
                      },
                    }}
                  />
                  <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                    <Button
                      type="submit"
                      disabled={formStatus === 'sending'}
                      sx={{ ...btnDark, mt: '16px', width: 129, minWidth: 129 }}
                    >
                      {formStatus === 'sending' ? '...' : t.absenden}
                    </Button>
                    {formStatus === 'sent' && (
                      <Typography sx={{ ...TYPO.bodyLight, color: '#fff', mt: '8px', mb: '40px' }}>
                        {t.sent}
                      </Typography>
                    )}
                    {formStatus === 'error' && (
                      <Typography sx={{ ...TYPO.bodyLight, color: '#e57373', mt: '8px', mb: '40px' }}>
                        {t.error}
                      </Typography>
                    )}
                    {formStatus === 'idle' && <Box sx={{ mb: '40px' }} />}
                  </Box>
                </Box>
                <Typography sx={{ ...TYPO.bodyLight, color: '#fff', maxWidth: 560, mt: '8px' }}>
                  {t.formPrivacyLead}{' '}
                  <Box
                    component={Link}
                    to={PATHS.datenschutz}
                    sx={{ color: '#fff', textDecoration: 'underline', textUnderlineOffset: '3px' }}
                  >
                    {t.formPrivacyLink}
                  </Box>
                  .
                </Typography>
              </Box>

              {/* ── Rechte Spalte: Kontakte, Links, Adresse ── */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'flex-start' },
                    gap: { xs: '40px', md: '48px' },
                    flex: 1,
                  }}
                >
                  <Box sx={{ maxWidth: 331 }}>
                    <Box sx={{ mb: { xs: '32px', md: '48px' } }}>
                      <Typography sx={{ ...TYPO.footerName, mb: 0, display: 'block' }}>
                        Zöllner Lorenz
                      </Typography>
                      <Typography sx={{ ...TYPO.footerName, mb: 0, display: 'block' }}>
                        Partnerschaft von Rechtsanwälten mbB
                      </Typography>
                      <Typography sx={{ ...TYPO.footerContactLine, display: 'block' }}>
                        Maximiliansplatz 12a · 80333 München
                      </Typography>
                      <Typography
                        component="a"
                        href="tel:+498924889752"
                        sx={{ ...TYPO.footerContactLine, display: 'block', textDecoration: 'none' }}
                      >
                        {t.footer.telefon}: +49 89 24889752
                      </Typography>
                      <Typography
                        component="a"
                        href="mailto:info@zoellner-lorenz.de"
                        sx={{ ...TYPO.footerContactLine, display: 'block', textDecoration: 'none' }}
                      >
                        info@zoellner-lorenz.de
                      </Typography>
                      <Typography sx={{ ...TYPO.footerContactLine, display: 'block', mt: '8px' }}>
                        {t.footer.termine}
                      </Typography>
                    </Box>

                    {team.map((member, index) => (
                      <Box
                        key={member.slug}
                        sx={{ mb: index === 0 ? { xs: '32px', md: '48px' } : 0 }}
                      >
                        <Typography sx={{ ...TYPO.footerName, mb: 0, display: 'block' }}>
                          {member.title}
                        </Typography>
                        <Typography sx={{ ...TYPO.footerContactLine, display: 'block' }}>
                          {member.description}
                        </Typography>
                        {member.email && (
                          <Typography
                            component="a"
                            href={`mailto:${member.email}`}
                            sx={{ ...TYPO.footerContactLine, display: 'block', textDecoration: 'none' }}
                          >
                            {member.email}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>

                  <Box
                    component="nav"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '21px',
                      alignItems: 'flex-start',
                      pb: '24px',
                    }}
                  >
                    {FOOTER_LINKS.map((link) => (
                      <Typography
                        key={link.label}
                        component={link.external ? 'a' : Link}
                        href={link.external ? link.href : undefined}
                        to={link.external ? undefined : link.href}
                        target={link.external ? '_blank' : undefined}
                        rel={link.external ? 'noopener noreferrer' : undefined}
                        sx={{
                          ...TYPO.footerLink,
                          textDecoration: 'none',
                          '&:hover': { opacity: 0.7 },
                        }}
                      >
                        {legalLabel(link.label)}
                      </Typography>
                    ))}
                    <Typography
                      component="button"
                      type="button"
                      onClick={openCookieSettings}
                      sx={{
                        ...TYPO.footerLink,
                        p: 0,
                        border: 0,
                        background: 'none',
                        color: '#f2f2f2',
                        cursor: 'pointer',
                        textAlign: 'left',
                        textDecoration: 'none',
                        '&:hover': { opacity: 0.7 },
                      }}
                    >
                      {legalLabel('Cookie-Einstellungen')}
                    </Typography>
                  </Box>
                </Box>

              </Box>
            </Box>

            {/* Footer bottom line — full width, single row on desktop */}
            <Box
              sx={{
                width: '100%',
                mt: { xs: '48px', lg: '40px' },
                pt: '16px',
                borderTop: '1px solid rgba(255,255,255,0.12)',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '14px', md: '18px' },
              }}
            >
              <Box
                component="img"
                src="/images/signet-zl-light.svg"
                alt=""
                aria-hidden
                width={28}
                height={28}
                loading="lazy"
                sx={{ width: { xs: 24, md: 28 }, height: 'auto', flexShrink: 0, opacity: 0.85 }}
              />
              <Typography
                component="p"
                sx={{
                  ...TYPO.footerAddress,
                  width: '100%',
                  m: 0,
                  whiteSpace: { xs: 'normal', lg: 'nowrap' },
                  fontSize: { xs: '13px', md: '14px' },
                  lineHeight: 1.5,
                }}
              >
                Zöllner Lorenz Partnerschaft von Rechtsanwälten mbB
                <Box component="span" sx={{ mx: { xs: '0.35em', md: '0.5em' }, opacity: 0.45 }}>
                  ·
                </Box>
                Maximiliansplatz 12a
                <Box component="span" sx={{ mx: { xs: '0.35em', md: '0.5em' }, opacity: 0.45 }}>
                  ·
                </Box>
                80333 München
                <Box component="span" sx={{ mx: { xs: '0.35em', md: '0.5em' }, opacity: 0.45 }}>
                  ·
                </Box>
                <Box
                  component="a"
                  href="tel:+498924889752"
                  sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { opacity: 0.75 } }}
                >
                  {t.footer.telefon}: +49 89 24889752
                </Box>
                <Box component="span" sx={{ mx: { xs: '0.35em', md: '0.5em' }, opacity: 0.45 }}>
                  ·
                </Box>
                <Box
                  component="a"
                  href="mailto:info@zoellner-lorenz.de"
                  sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { opacity: 0.75 } }}
                >
                  info@zoellner-lorenz.de
                </Box>
              </Typography>
            </Box>
        </Box>
      </Box>
    </>
  );
};

export default HomePage;
